import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ToastContainerDirective } from 'ngx-toastr';
import { UserService } from '../../../services/user.service';
import { AtlasToastService } from '../../../services/atlas-toast.service';
import { faEye, faEyeSlash, faCopy } from '@fortawesome/free-solid-svg-icons';
import { checkPassword } from '@utils/validators';
import { SecurityService } from '@services/security.service';
import { Clipboard } from '@angular/cdk/clipboard';

function matchingPasswords(matchTo: string): (AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    return !!control.parent && !!control.parent.value && control.value === control.parent.get(matchTo).value
      ? null
      : { notEquivalent: true };
  };
}

@Component({
  selector: 'wsb-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
  standalone: false
})
export class ChangePasswordComponent implements OnInit, AfterViewInit {
  @ViewChild(ToastContainerDirective) modalToastContainer: ToastContainerDirective;
  @ViewChild(ToastContainerDirective) toastContainer: ToastContainerDirective;
  @Input() public typeResetPassword: 'web' | 'api';
  passwordForm: UntypedFormGroup;
  faPasswordOldIconShow = faEye;
  showPasswordOld = false;
  faPasswordNewIconShow = faEye;
  showPasswordNew = false;
  faPasswordRepeatIconShow = faEye;
  showPasswordRepeat = false;
  faCopyIcon = faCopy;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private atlasToast: AtlasToastService,
    private securityService: SecurityService,
    private clipboard: Clipboard
  ) {}

  ngOnInit() {
    this.passwordForm = this.formBuilder.group({
      currentPassword: this.formBuilder.control('', []),
      newPassword: this.formBuilder.control('', [Validators.required]),
      confirmNewPassword: this.formBuilder.control('', [])
    });

    if (this.typeResetPassword === 'web') {
      this.passwordForm.get('currentPassword').setValidators([Validators.required]);
      this.passwordForm.get('currentPassword').updateValueAndValidity();
      this.passwordForm.get('confirmNewPassword').setValidators([Validators.required, matchingPasswords('newPassword')]);
      this.passwordForm.get('confirmNewPassword').updateValueAndValidity();
      this.passwordForm.get('newPassword').setValidators([Validators.required, checkPassword()]);
      this.passwordForm.get('newPassword').updateValueAndValidity();
    }
    if (this.typeResetPassword === 'api') {
      const randomPassword = this.securityService.generateRandomPassword();
      this.passwordForm.get('newPassword').setValue(randomPassword);
      this.passwordForm.markAsDirty();
      this.passwordForm.markAsTouched();
    }
  }

  doSubmit() {
    this.passwordForm.disable();
    switch (this.typeResetPassword) {
      case 'web':
        this.callUpdatePasswordWEB();
        break;
      case 'api':
        this.callUpdatePasswordAPI();
        break;
    }
  }

  private callUpdatePasswordWEB() {
    this.userService
      .updatePassword({
        currentPassword: this.passwordForm.controls.currentPassword.value,
        newPassword: this.passwordForm.controls.newPassword.value,
        newPasswordControl: this.passwordForm.controls.confirmNewPassword.value
      })
      .subscribe(
        updatedUser => this.activeModal.close(updatedUser),
        error => {
          this.passwordForm.enable();
          this.atlasToast.error('common_error', null, null, this.modalToastContainer);
        },
        () => {
          this.atlasToast.success('reset_ok', null);
        }
      );
  }

  private callUpdatePasswordAPI() {
    this.userService
      .updatePasswordAPI({
        newPassword: this.passwordForm.controls.newPassword.value
      })
      .subscribe(
        updatedUser => this.activeModal.close(updatedUser),
        error => {
          this.passwordForm.enable();
          this.atlasToast.error('common_error', null, null, this.modalToastContainer);
        },
        () => {
          this.atlasToast.success('reset_ok', null);
        }
      );
  }

  showHidePassWordOld() {
    this.showPasswordOld = !this.showPasswordOld;
    if (this.showPasswordOld) {
      this.faPasswordOldIconShow = faEyeSlash;
    } else {
      this.faPasswordOldIconShow = faEye;
    }
  }

  showHidePassWordNew() {
    this.showPasswordNew = !this.showPasswordNew;
    if (this.showPasswordNew) {
      this.faPasswordNewIconShow = faEyeSlash;
    } else {
      this.faPasswordNewIconShow = faEye;
    }
  }

  showHidePassWordRepeat() {
    this.showPasswordRepeat = !this.showPasswordRepeat;
    if (this.showPasswordRepeat) {
      this.faPasswordRepeatIconShow = faEyeSlash;
    } else {
      this.faPasswordRepeatIconShow = faEye;
    }
  }

  copyValueToClipBoard(value) {
    if (value) {
      const success = this.clipboard.copy(value);
      if (success) {
        this.atlasToast.success('common_success', null);
      }
    }
  }

  cancel() {
    this.activeModal.dismiss();
  }
  ngAfterViewInit(): void {
    let labelInfo = '';
    switch (this.typeResetPassword) {
      case 'web':
        labelInfo = 'change_password_alert';
        break;
      case 'api':
        labelInfo = 'change_apipassword_alert';
        break;
    }
    this.atlasToast.info(labelInfo, '', -1, this.toastContainer);
  }
}
